/**
 * Verving — svindelsikker kjerne (UX-prinsipp #3 Reciprocity — se
 * docs/plan-ux-principles-retention.md). Dobbeltsidig gratis premium-måned:
 *  - VERVET får måneden umiddelbart ved oppretting via vennelenke (gaven kommer først).
 *  - VERVER får måneden når vervet har gjort SIN FØRSTE INNSJEKK (ekte aktivering,
 *    svindelsikkert, + goal-gradient for verver).
 *
 * Dette er den RENE, testbare kjernen: kode-generering, lenkebygging og reward-
 * berettigelse (state machine). Persistering + faktisk premium-grant + klient-flater
 * er egne deler (functions/index.js, firestore.rules, invite-UI) — de bygger på denne.
 *
 * Ingen Date/Math.random her: koden utledes deterministisk fra uid (idempotent — samme
 * bruker gir alltid samme kode).
 */

const CODE_LENGTH = 7;
const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'; // base36, uppercased

/** Deterministisk FNV-1a-hash → base36. Kort, URL-trygg, ikke-reversibel nok for en kode. */
export function generateReferralCode(uid: string): string {
  if (!uid) return '';
  // FNV-1a 32-bit
  let h = 0x811c9dc5;
  for (let i = 0; i < uid.length; i++) {
    h ^= uid.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  // Bland inn lengden så korte/like uid-er sprer seg bedre, og fyll ut til CODE_LENGTH.
  let n = (h ^ (uid.length * 0x9e3779b1)) >>> 0;
  let code = '';
  for (let i = 0; i < CODE_LENGTH; i++) {
    code += ALPHABET[n % ALPHABET.length];
    n = Math.floor(n / ALPHABET.length);
    if (n === 0) n = (h >>> (i + 1)) ^ (i + 1); // re-seed så vi ikke stopper på 0
    n = n >>> 0;
  }
  return code;
}

/** Bygg en delbar vervelenke. baseUrl uten etterslepende skråstrek. */
export function buildReferralLink(baseUrl: string, code: string): string {
  const clean = baseUrl.replace(/\/+$/, '');
  return `${clean}/?ref=${encodeURIComponent(code)}`;
}

export interface ReferralState {
  /** Vervet er samme person som verver (selv-verving) → aldri belønn. */
  isSelfReferral: boolean;
  /** En gyldig vervet-bruker er opprettet med koden. */
  refereeExists: boolean;
  /** Når vervet gjorde sin første innsjekk (undefined = ikke ennå). */
  refereeFirstCheckinAt?: number;
  /** Når vervets umiddelbare velkomst-belønning ble gitt (undefined = ikke gitt). */
  refereeRewardedAt?: number;
  /** Når ververs belønning ble gitt (undefined = ikke gitt). */
  referrerRewardedAt?: number;
}

export interface ReferralRewardDecision {
  /** Skal vervet få premium-måneden nå (umiddelbart ved oppretting)? */
  grantRefereeReward: boolean;
  /** Skal verver få premium-måneden nå (etter vervets første innsjekk)? */
  grantReferrerReward: boolean;
  blockedReason?: 'self_referral' | 'no_referee';
}

/**
 * Ren beslutning om hvem som skal belønnes nå. Idempotent: allerede-belønnede gir false,
 * så gjentatte kall (f.eks. ved hver innsjekk) aldri dobbelt-belønner.
 */
export function evaluateReferralReward(state: ReferralState): ReferralRewardDecision {
  if (state.isSelfReferral) {
    return { grantRefereeReward: false, grantReferrerReward: false, blockedReason: 'self_referral' };
  }
  if (!state.refereeExists) {
    return { grantRefereeReward: false, grantReferrerReward: false, blockedReason: 'no_referee' };
  }

  const grantRefereeReward = !state.refereeRewardedAt;
  const grantReferrerReward = !!state.refereeFirstCheckinAt && !state.referrerRewardedAt;

  return { grantRefereeReward, grantReferrerReward };
}
